"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Building2, ShoppingBag, Briefcase, MapPin, ArrowRight, UserCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
const audienceItems = [{
  title: "B2B and SaaS",
  description: "Buyers use AI tools to shortlist suppliers. I focus on comparison questions, feature breakdowns, pricing transparency, and compliance context so AI systems understand how your service compares.",
  linkText: "B2B SEO & lead generation",
  linkTo: "/B2BSeoLeadGenerationPage",
  icon: Briefcase
}, {
  title: "Ecommerce brands",
  description: "AI can surface products, buying guides, and category pages for recommendation-style questions. I align product descriptions, category hubs, FAQs, technical specifications, and genuine review data.",
  linkText: "Ecommerce SEO strategies",
  linkTo: "/EcommerceSeoPage",
  icon: ShoppingBag
}, {
  title: "UK SMEs and growth businesses",
  description: "Most gains for SMEs come from restructuring and clarifying existing content before creating new material, fixing technical crawl barriers, and making core service information clear.",
  linkText: "Explore SEO audits",
  linkTo: "/SeoAuditsPage",
  icon: Building2
}, {
  title: "Local and professional services",
  description: "AI models look for consistent local business data, customer reviews, and clear service-area definitions across directories and websites to recommend regional providers.",
  linkText: "Local SEO solutions",
  linkTo: "/LocalSEOPage",
  icon: MapPin
}];
export default function AudienceDifferentiators() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="audienceanddifferentiators" data-nav="light" className="bg-background text-foreground py-20 md:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl space-y-20">
        {/* Subsection 1: Who AI search optimisation is for */}
        <div>
          <div className="max-w-3xl mb-12">
            <Badge variant="secondary" className="mb-4">
              Target audience
            </Badge>
            <h2 className="font-serif font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-foreground mb-4">
              Who AI search optimisation is for
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
              I help businesses whose prospective customers use generative tools alongside search engines to research solutions, compare providers, and make purchasing decisions.
            </p>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.2
        }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {audienceItems.map((item, i) => {
            const IconComp = item.icon;
            return <motion.div key={item.title} data-index={i} variants={itemVariants} whileHover={shouldReduceMotion ? {} : {
              y: -4
            }} className="h-full">
                  <Card className="h-full border border-border bg-card hover:border-primary/40 transition-[border-color,box-shadow] duration-300 shadow-xs flex flex-col justify-between">
                    <CardHeader className="space-y-3 pb-2">
                      <div className="w-12 h-12 rounded-lg bg-secondary/80 flex items-center justify-center text-primary">
                        <IconComp className="w-6 h-6" />
                      </div>
                      <CardTitle className="text-lg font-serif font-bold text-foreground">
                        {item.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4 pt-0">
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        {item.description}
                      </p>
                      <div className="pt-2">
                        <Link to={item.linkTo} className="inline-flex items-center text-xs font-semibold text-primary hover:text-primary/80 transition-colors group">
                          <span>{item.linkText}</span>
                          <ArrowRight className="ml-1 w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </motion.div>
        </div>

        {/* Subsection 2: Who does the work */}
        <div className="pt-8 border-t border-border/60">
          <div className="rounded-2xl bg-card border border-border p-8 sm:p-12 max-w-4xl mx-auto shadow-xs">
            <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-6">
              <UserCheck className="w-6 h-6" />
            </div>

            <h2 className="font-serif font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-foreground mb-4">
              Who does the work
            </h2>

            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed mb-6">
              AI search strategy, audits and recommendations are written and led by me, Yassine Essaadi, an independent SEO consultant. I explain what I find in plain language, agree priorities with you, and either hand over implementation notes for your developers and writers or support the work hands-on, depending on scope.
            </p>

            <p className="text-sm sm:text-base text-foreground font-medium mb-8">
              If you are considering{" "}
              <Link to="/HireSeoConsultantPage" className="text-primary underline underline-offset-4 hover:text-primary/80">
                hiring an SEO consultant
              </Link>
              , you work with me directly at every stage.
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-4 pt-4 border-t border-border/60">
              <Button size="lg" asChild className="w-full sm:w-auto px-8 shadow-sm">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild className="w-full sm:w-auto">
                <Link to="/CaseStudiesPage">
                  View Case Studies
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}